import React, { useMemo, useState } from 'react';
import './LoginPage.css';
import './LoginHelpPage.css';
import zynityLogo from '../../assets/images/zynity_logo.png';
import { requestPasswordResetEmail } from '../../api/auth/passwordReset';

export const LoginHelpPage: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const menuItems = useMemo(
    () => [
      { label: 'Public Site', href: 'https://zynity.com/' },
      { label: 'Help Request', href: '/help-request' },
      { label: 'Login', href: '/site/login' },
      { label: 'Sign Up', href: '/people/register' },
    ],
    [],
  );

  const toggleMenu = () => setIsMenuOpen((open) => !open);
  const handleLinkClick = () => setIsMenuOpen(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);
    setIsSubmitting(true);

    requestPasswordResetEmail(email)
      .then((data) => {
        setMessage(
          data?.message ||
            'A password reset link has been sent to the email address we have on file. If you do not receive it shortly, please try again or use the Help Request option.',
        );
      })
      .catch((err: any) => {
        const msg =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          'Unable to send reset email. Please try again.';
        setError(msg);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <div className="login-page login-help-page">
      <header className="login-navbar">
        <a className="navbar-logo" href="https://apps.zynity.com" aria-label="Zynity home">
          <img src={zynityLogo} alt="Zynity logo" />
        </a>
        <div className="navbar-menu">
          <button
            type="button"
            className="menu-button"
            aria-haspopup="true"
            aria-expanded={isMenuOpen}
            onClick={toggleMenu}
          >
            <span className="menu-icon" aria-hidden="true">
              &#9776;
            </span>
          </button>
          {isMenuOpen && (
            <div className="menu-dropdown">
              {menuItems.map((item) => (
                <a key={item.label} className="menu-item" href={item.href} onClick={handleLinkClick}>
                  {item.label}
                </a>
              ))}
            </div>
          )}
        </div>
      </header>

      <main className="login-help-content">
        <h1 className="login-help-title">Login Help</h1>

        <p className="login-help-text">
          If your username/password combination is not working, you can
        </p>
        <p className="login-help-text login-help-text--emphasis">reset your password</p>
        <p className="login-help-text">by submitting your email address below.</p>

        <p className="login-help-text login-help-text--spaced">
          You will receive an email with a link to reset your password.
        </p>

        <form className="login-help-form" onSubmit={handleSubmit}>
          <input
            className="login-help-input"
            type="email"
            name="email"
            placeholder="Email Address *"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
          />
          {message && (
            <div className="login-help-alert login-help-alert--success" role="status">
              {message}
            </div>
          )}
          {error && (
            <div className="login-help-alert login-help-alert--error" role="alert">
              {error}
            </div>
          )}
          <button className="login-help-button" type="submit" disabled={isSubmitting || !email}>
            {isSubmitting ? 'SENDING...' : 'RESET PASSWORD'}
          </button>
        </form>

        <section className="login-help-assist" aria-label="Additional support">
          <p className="login-help-assist-line">
            <span className="login-help-face" aria-hidden="true">
              &#9785;
            </span>{' '}
            Still need help?&nbsp; Glad to assist.&nbsp;
            <span className="login-help-face" aria-hidden="true">
              &#9786;
            </span>
          </p>
          <p className="login-help-assist-line">
            Click here &#8680;{' '}
            <a className="login-help-request" href="/help-request">
              Help Request
            </a>{' '}
            and tell us what's happening.
          </p>
          <p className="login-help-assist-line">Support will get right to it.</p>
        </section>

        <p className="login-help-footer">Copyright © 2015-2025 Zynity. All rights reserved.</p>
      </main>
    </div>
  );
};
